import React from "react";
import {
  Typography,
  Box,
  Button,
  TableCell,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Checkbox,
  FormGroup,
  FormLabel,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Snackbar,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon
} from "@mui/icons-material";
import SortableTable from "../components/SortableTable";
import {
  useJobManagement,
  getScheduleTypeLabel,
  getStatusColor,
  getScheduleTypeFromExpression,
  generateCronExpression,
} from "../hooks/useJobManagement";

function JobManagement() {
  const {
    jobs,
    jobsLoading,
    jobsError,
    eshops,
    eshopsLoading,
    openDialog,
    selectedJob,
    formData,
    setFormData,
    cronConfig,
    setCronConfig,
    selectedJobIds,
    setSelectedJobIds,
    snackbar,
    handleCloseSnackbar,
    createJobMutation,
    updateJobMutation,
    executeJobMutation,
    bulkExecuteMutation,
    handleOpenDialog,
    handleCloseDialog,
    handleSubmit,
    handleDelete,
    handleExecute,
    handleBulkExecute,
    handleToggle,
  } = useJobManagement();

  if (jobsLoading || eshopsLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          Job Management
        </Typography>
        <Box>
          {selectedJobIds.length > 0 && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<PlayIcon />}
              onClick={handleBulkExecute}
              sx={{ mr: 2 }}
              disabled={bulkExecuteMutation.isLoading}
            >
              Run Selected ({selectedJobIds.length})
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Job
          </Button>
        </Box>
      </Box>

      {jobsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading jobs: {jobsError.message}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Jobs
              </Typography>
              <Typography variant="h4">{jobs?.length || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Jobs
              </Typography>
              <Typography variant="h4">
                {jobs?.filter((j) => j.enabled).length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Manual Jobs
              </Typography>
              <Typography variant="h4">
                {jobs?.filter((j) => !j.schedule_expression).length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Scheduled Jobs
              </Typography>
              <Typography variant="h4">
                {jobs?.filter((j) => j.schedule_expression).length || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <SortableTable
        data={jobs || []}
        enableSelection={true}
        onSelectionChange={setSelectedJobIds}
        selectionKey="id"
        defaultOrderBy="job_name"
        defaultOrder="asc"
        columns={[
          { id: 'job_name', label: 'Name' },
          { id: 'eshop_name', label: 'E-shop' },
          { id: 'schedule_type', label: 'Schedule Type' },
          { id: 'enabled', label: 'Status' },
          { id: 'last_run', label: 'Last Run' },
          { id: 'actions', label: 'Actions', sortable: false },
        ]}
        renderRow={(job) => {
          const eshop = eshops?.find((e) => e.id === job.eshop_id);

          return (
            <TableRow key={job.id}>
              <TableCell>{job.job_name}</TableCell>
              <TableCell>{eshop?.name || `Unknown (ID: ${job.eshop_id})`}</TableCell>
              <TableCell>
                <Chip
                  label={getScheduleTypeLabel(getScheduleTypeFromExpression(job.schedule_expression))}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Chip
                  label={job.enabled ? "Active" : "Inactive"}
                  color={getStatusColor(job.enabled ? "active" : "inactive")}
                  size="small"
                />
              </TableCell>
              <TableCell>
                {job.last_run
                  ? new Date(job.last_run).toLocaleString()
                  : "Never"}
              </TableCell>
              <TableCell>
                <IconButton
                  onClick={() => handleExecute(job.id)}
                  color="secondary"
                  title="Execute job"
                  disabled={executeJobMutation.isLoading}
                >
                  <PlayIcon />
                </IconButton>
                <IconButton
                  onClick={() => handleToggle(job)}
                  color={job.enabled ? "warning" : "success"}
                  title={job.enabled ? "Disable job" : "Enable job"}
                >
                  {job.enabled ? <StopIcon /> : <PlayIcon />}
                </IconButton>
                <IconButton
                  onClick={() => handleOpenDialog(job)}
                  color="primary"
                  title="Edit job"
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  onClick={() => handleDelete(job.id)}
                  color="error"
                  title="Delete job"
                >
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          );
        }}
      />

      {/* Job Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedJob ? "Edit Job" : "Create New Job"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Job Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>E-shop</InputLabel>
              <Select
                value={formData.eshop_id}
                onChange={(e) =>
                  setFormData({ ...formData, eshop_id: e.target.value })
                }
              >
                {eshops?.map((eshop) => (
                  <MenuItem key={eshop.id} value={eshop.id}>
                    {eshop.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Schedule Type</InputLabel>
              <Select
                value={formData.schedule_type}
                onChange={(e) =>
                  setFormData({ ...formData, schedule_type: e.target.value })
                }
              >
                <MenuItem value="manual">Manual</MenuItem>
                <MenuItem value="interval">Interval</MenuItem>
                <MenuItem value="cron">Cron</MenuItem>
              </Select>
            </FormControl>

            {formData.schedule_type === "interval" && (
              <TextField
                fullWidth
                label="Interval Expression"
                value={formData.schedule_expression}
                onChange={(e) =>
                  setFormData({ ...formData, schedule_expression: e.target.value })
                }
                placeholder="e.g., 5m (5 minutes), 1h (1 hour), 30s (30 seconds)"
                sx={{ mb: 2 }}
              />
            )}

            {formData.schedule_type === "cron" && (
              <Box sx={{
                mb: 2,
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'action.hover'
              }}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Cron Preset</InputLabel>
                  <Select
                    value={cronConfig.type}
                    onChange={(e) => setCronConfig({ ...cronConfig, type: e.target.value })}
                  >
                    <MenuItem value="daily">Every Day</MenuItem>
                    <MenuItem value="weekly">Specific Days</MenuItem>
                    <MenuItem value="custom">Custom Expression</MenuItem>
                  </Select>
                </FormControl>

                {cronConfig.type !== "custom" && (
                  <TextField
                    fullWidth
                    label="Time"
                    type="time"
                    value={cronConfig.time}
                    onChange={(e) => setCronConfig({ ...cronConfig, time: e.target.value })}
                    sx={{ mb: 2 }}
                    InputLabelProps={{ shrink: true }}
                  />
                )}

                {cronConfig.type === "weekly" && (
                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <FormLabel component="legend">Run on these days</FormLabel>
                    <FormGroup row>
                      {[
                        { label: 'Mon', value: 1 },
                        { label: 'Tue', value: 2 },
                        { label: 'Wed', value: 3 },
                        { label: 'Thu', value: 4 },
                        { label: 'Fri', value: 5 },
                        { label: 'Sat', value: 6 },
                        { label: 'Sun', value: 0 },
                      ].map((day) => (
                        <FormControlLabel
                          key={day.value}
                          control={
                            <Checkbox
                              size="small"
                              checked={cronConfig.days.includes(day.value)}
                              onChange={(e) => {
                                const newDays = e.target.checked
                                  ? [...cronConfig.days, day.value]
                                  : cronConfig.days.filter((d) => d !== day.value);
                                setCronConfig({ ...cronConfig, days: newDays });
                              }}
                            />
                          }
                          label={day.label}
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                )}

                {cronConfig.type === "custom" && (
                  <TextField
                    fullWidth
                    label="CRON Expression"
                    value={formData.schedule_expression}
                    onChange={(e) =>
                      setFormData({ ...formData, schedule_expression: e.target.value })
                    }
                    placeholder="e.g., 0 12 * * *"
                    helperText="Format: minute hour day-of-month month day-of-week"
                  />
                )}

                {cronConfig.type !== "custom" && (
                  <Typography variant="caption" color="textSecondary">
                    Generated Expression: {generateCronExpression(cronConfig, formData.schedule_expression)}
                  </Typography>
                )}
              </Box>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData({ ...formData, is_active: e.target.checked })
                  }
                />
              }
              label="Active"
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              !formData.name ||
              !formData.eshop_id ||
              createJobMutation.isLoading ||
              updateJobMutation.isLoading
            }
          >
            {selectedJob ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
      />
    </Box>
  );
}

export default JobManagement;
